import React, { createContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation, useMatch, useNavigate } from 'react-router-dom';
import qs from 'query-string';

import {
    useImpersonateUserMutation,
    useGetCurrentUserQuery,
    useCognitoSignInMutation,
} from 'api/main';
import { googleSelectors, menuSelectors, userSelectors } from 'reducers';
import { googlePlacesInitialized } from 'reducers/google';
import AppRoutes, { protectedRoutes, publicRoutes } from 'routes';
import {
    distributorType,
    environments,
    roleCategories,
    RoleNames,
    userChallenges,
} from 'utils/constants';
import {
    useCognitoContextProvider,
    useMatchAtLeastOneRole,
    useSubscriptionFormSignatureStatus,
    useUserRoleMatchCategory,
    useTunnelFlow,
} from 'utils/hooks';
import { loadScript } from 'utils/helpers';
import { sortRoutesByRole, routesFilter } from 'routes/routeMappings';

import SignatureChoiceDialog from 'containers/subscriptions/SignatureChoiceDialog';
import {
    TopBar,
    Sidebar,
    Footer,
    BigLoader,
    CustomNotification,
    ConfirmLogoutDialog,
    NotificationDialog,
} from 'components';
import { Box } from '@mui/material';
import { setNavigate } from 'utils/navigation';
import { HubspotScript } from './hubspot/HubspotScript';
import { HubspotPageTracking } from './hubspot/HubspotPageTracking';

window.googleInitCallback = () => {
    if (process.env.NODE_ENV === environments.DEV) console.log('GOOGLE PLACES INITIALIZED');
};

const GOOGLE_MAPS_BASE_URL = 'https://maps.googleapis.com/maps/api/js';
const googleMapsBaseOptions = {
    key: process.env.REACT_APP_MAPS_API_KEY,
    language: 'fr',
    libraries: 'places',
    callback: 'googleInitCallback',
};

export const CognitoContext = createContext({ isSignedIn: false });

const getInvestorFallbackPath = ({
    hasInProgressSubscription,
    hasFinishedSubscription,
    userChallenge,
    isImpersonatedByDistributor,
}) => {
    if (hasInProgressSubscription && !hasFinishedSubscription) return '/sign-up';
    if (userChallenge === userChallenges.NEW_PASSWORD_REQUIRED) return '/setup-password';
    if (isImpersonatedByDistributor) return '/new-subscription';
    return '/dashboard';
};

function MainContainer({
    loggedIn,
    hasInProgressSubscription,
    isImpersonatedByDistributor,
    hasFinishedSubscription,
    userIsServicer,
    userIsDistributor,
    userIsInvestor,
    currentSubscription,
    filteredRoutes,
    userChallenge,
    isTunnelFlow,
}) {
    const location = useLocation();

    const loggedInPath = useMemo(() => {
        if (userIsInvestor) {
            return getInvestorFallbackPath({
                hasInProgressSubscription,
                hasFinishedSubscription,
                userChallenge,
                isImpersonatedByDistributor,
            });
        } else if (hasInProgressSubscription && !userIsServicer) {
            return '/sign-up';
        } else if (userChallenge === userChallenges.NEW_PASSWORD_REQUIRED) {
            return '/setup-password';
        } else if (userIsDistributor) {
            return '/homepage';
        } else {
            return '/dashboard';
        }
    }, [
        hasInProgressSubscription,
        hasFinishedSubscription,
        userChallenge,
        userIsDistributor,
        userIsServicer,
        userIsInvestor,
        isImpersonatedByDistributor,
    ]);

    const returnPath =
        location.pathname !== '/' ? `?returnTo=${encodeURIComponent(window.location.href)}` : '';
    const fallbackPath = loggedIn ? loggedInPath : `/sign-in${returnPath}`;
    const [, { isLoading: impersonateLoader }] = useImpersonateUserMutation({
        fixedCacheKey: 'user-impersionation',
    });

    return (
        <Box flex="1 1" height={isTunnelFlow ? '100%' : undefined}>
            <AppRoutes
                loggedIn={loggedIn}
                role={currentSubscription?.role?.name}
                routes={!loggedIn ? [...publicRoutes] : [...filteredRoutes]}
                fallbackPath={impersonateLoader ? null : fallbackPath}
            />
        </Box>
    );
}

MainContainer.propTypes = {
    loggedIn: PropTypes.bool,
    hasInProgressSubscription: PropTypes.bool,
    hasFinishedSubscription: PropTypes.bool,
    userIsServicer: PropTypes.bool,
    userIsDistributor: PropTypes.bool,
    userIsInvestor: PropTypes.bool,
    currentSubscription: PropTypes.object,
    filteredRoutes: PropTypes.array,
    userChallenge: PropTypes.string,
    isTunnelFlow: PropTypes.bool,
};

function App() {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const loggedIn = useSelector(userSelectors.getLoggedInStatus);
    const googleLibrary = useSelector(googleSelectors.getGoogleLibrary);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription); // the "main" finished subscription
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription); // the current in_progress subscription
    const internalUser = useSelector(userSelectors.getInternalUser);
    const sidebarOpen = useSelector(menuSelectors.getMenuOpenState);

    const [, { data: cognitoResponse }] = useCognitoSignInMutation({
        fixedCacheKey: 'cognito-sign-in',
    });
    const userIsChallenged = cognitoResponse?.nextStep?.signInStep && !cognitoResponse?.isSignedIn;

    const { isLoading: getUserLoader } = useGetCurrentUserQuery();
    const cognitoContext = useCognitoContextProvider();

    const [googlePlacesTimestamp, setGooglePlacesTimestamp] = useState(Date.now());

    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const hasAssignedNetwork = !!internalUser?.adminNetworks?.length;

    useEffect(() => {
        // we're setting this for outside component usage
        setNavigate(navigate);
    }, [navigate]);

    useEffect(() => {
        const googleServicesRefresher = setInterval(() => {
            setGooglePlacesTimestamp(Date.now());
        }, 3 * 36e5); // 3h interval
        return () => {
            clearInterval(googleServicesRefresher);
        };
    }, []);

    // temporary side effect to redirect from old domain
    useEffect(() => {
        const oldDomain = 'peqan.fr';
        const newDomain = 'petech.fr';

        const currentUrl = new URL(window.location.href);
        const currentHost = currentUrl.hostname;

        // only redirect if we're on peqan.fr or its subdomains
        if (currentHost.endsWith(oldDomain) && !currentHost.endsWith(newDomain)) {
            const subdomain = currentHost.slice(0, -oldDomain.length).replace(/\.$/, '');
            const newHost = subdomain ? `${subdomain}.${newDomain}` : newDomain;

            currentUrl.hostname = newHost;

            window.location.replace(currentUrl.href);
        }
    }, []);

    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);
    const userIsDistributor = useUserRoleMatchCategory([
        roleCategories.ADMIN_DISTRIBUTORS,
        roleCategories.DISTRIBUTORS,
    ]);
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);
    const hasInProgressSubscription = useSelector(userSelectors.getInProgressSubscriptionStatus);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');

    const isNewSubscription = useMemo(
        () => location.pathname.includes('new-subscription'),
        [location]
    );
    const isImpersonatedByDistributor = useMemo(
        () =>
            [RoleNames.ADMIN_CIF, RoleNames.PSI, RoleNames.COMMERCIALISATEUR].includes(
                internalUser?.impersonatorRole
            ),
        [internalUser]
    );

    const subscriberSignedBS = useSubscriptionFormSignatureStatus(tunnelSubscription);

    const distributorPreviewClientSpace = useMemo(
        () => currentSubscription?.distributorCompany?.documentsValidated,
        [currentSubscription]
    );

    const filteredRoutes = useMemo(() => {
        if (cognitoResponse?.nextStep?.signInStep === userChallenges.NEW_PASSWORD_REQUIRED) {
            return publicRoutes.filter(r => r.path.includes('setup-password'));
        }
        if (userIsInvestor) {
            if (
                hasInProgressSubscription &&
                !hasFinishedSubscription &&
                !userIsServicer &&
                !subscriberSignedBS
            ) {
                // only allow tunnel access if not completed
                return protectedRoutes.filter(
                    r =>
                        r.path.includes('sign-up') ||
                        r.path.includes('conditions-generales') ||
                        r.path.includes('abandonner')
                );
            }

            if (
                hasInProgressSubscription &&
                !userIsServicer &&
                subscriberSignedBS && // on in-progress subscription
                !hasFinishedSubscription
            ) {
                return protectedRoutes
                    .filter(
                        r =>
                            r.path.includes('dashboard') ||
                            r.path.includes('portfolio') ||
                            r.path.includes('investor-documents') ||
                            r.path.includes('conditions-generales') ||
                            r.path.includes('sign-up-success') ||
                            r.path.includes('sign-up-error')
                    )
                    .filter(routesFilter(currentSubscription?.role?.name));
            }

            if (isImpersonatedByDistributor) {
                return protectedRoutes.filter(r => r.path.includes('new-subscription'));
            }
            if (hasFinishedSubscription) {
                // can't access tunnel if subscription is finished
                return protectedRoutes
                    .filter(r => !r.path.includes('sign-up/'))
                    .filter(routesFilter(currentSubscription?.role?.name));
            }
        } else if (userIsDistributor) {
            if (!hasFinishedSubscription && !distributorPreviewClientSpace) {
                // only allow tunnel access if not completed
                return protectedRoutes.filter(
                    r =>
                        r.path.includes('onboarding') ||
                        r.path.includes('conditions-generales') ||
                        r.path.includes('abandonner')
                );
            } else if (!hasFinishedSubscription && distributorPreviewClientSpace) {
                const filteredRoutes = protectedRoutes
                    .filter(
                        r =>
                            r.path.includes('homepage') ||
                            r.path.includes('products') ||
                            r.path.includes('distributor-documents') ||
                            r.path.includes('distributor-users') ||
                            r.path.includes('conditions-generales')
                    )
                    .filter(routesFilter(currentSubscription?.role?.name, false));
                return sortRoutesByRole(filteredRoutes, currentSubscription?.role?.category?.name);
            } else if (
                currentSubscription?.distributorCompany?.external ||
                (!currentSubscription?.distributorCompany?.commissionsMenu &&
                    currentSubscription?.distributorCompany?.type !== distributorType.STANDARD)
            ) {
                const filteredRoutes = protectedRoutes
                    .filter(r => !r.path.includes('distributor-remunerations'))
                    .filter(routesFilter(currentSubscription?.role?.name, false));
                return sortRoutesByRole(filteredRoutes, currentSubscription?.role?.category?.name);
            } else {
                const filteredRoutes = protectedRoutes
                    .filter(r => !r.path.includes('onboarding'))
                    .filter(routesFilter(currentSubscription?.role?.name));
                return sortRoutesByRole(filteredRoutes, currentSubscription?.role?.category?.name);
            }
        } else if (loggedIn) {
            const filteredRoutes = protectedRoutes
                .filter(r => !r.path.includes('sign-up/') && !r.path.includes('onboarding')) // no other users need to access the tunnel
                .filter(routesFilter(currentSubscription?.role?.name));
            return sortRoutesByRole(filteredRoutes, currentSubscription?.role?.category?.name);
        }
        return protectedRoutes.filter(routesFilter(currentSubscription?.role?.name));
    }, [
        currentSubscription,
        hasFinishedSubscription,
        hasInProgressSubscription,
        userIsServicer,
        userIsDistributor,
        userIsInvestor,
        loggedIn,
        subscriberSignedBS,
        distributorPreviewClientSpace,
        cognitoResponse,
        isImpersonatedByDistributor,
    ]);
    const currentRouteIsPublic = useMemo(
        () => !!publicRoutes.find(x => matchPath(x, location.pathname)),
        [location]
    );

    const currentRouteIsProtected = useMemo(
        () => !!protectedRoutes.find(x => matchPath(x, location.pathname) && x.protected),
        [location]
    );

    // Determine when to show the AppBar
    const showAppBar = useMemo(() => {
        // Don't show AppBar during password setup challenge
        if (cognitoResponse?.nextStep?.signInStep === userChallenges.NEW_PASSWORD_REQUIRED)
            return false;

        // Don't show AppBar if user is challenged (except for password setup)
        if (
            userIsChallenged &&
            cognitoResponse?.nextStep?.signInStep !== userChallenges.NEW_PASSWORD_REQUIRED
        )
            return false;

        // Show AppBar for tunnel flows (sign-up, onboarding, abandonment)
        if (
            location.pathname.includes('sign-up') ||
            location.pathname.includes('onboarding') ||
            location.pathname.includes('abandonner') ||
            location.pathname.includes('auth') ||
            location.pathname.includes('password')
        ) {
            return true;
        }

        // Don't show AppBar if user is not logged in
        if (!loggedIn) return false;

        // Show AppBar for non-investor/non-distributor users (servicers, admins, etc.)
        if (!userIsInvestor && !userIsDistributor) return true;

        // Show AppBar for investors who have signed BS or finished subscription
        if (userIsInvestor && (subscriberSignedBS || hasFinishedSubscription)) return true;

        // Show AppBar for distributors who have preview client space or finished subscription
        if (userIsDistributor && (distributorPreviewClientSpace || hasFinishedSubscription))
            return true;

        return currentRouteIsProtected;
    }, [
        loggedIn,
        cognitoResponse,
        userIsChallenged,
        userIsInvestor,
        userIsDistributor,
        subscriberSignedBS,
        hasFinishedSubscription,
        distributorPreviewClientSpace,
        location.pathname,
        currentRouteIsProtected,
    ]);

    // Determine if we're in tunnel flows that need their own overflow handling
    const isTunnelFlow = useTunnelFlow();

    useEffect(() => {
        let mounted = true;
        if (!document.querySelector('#google-maps')) {
            loadScript(
                `${GOOGLE_MAPS_BASE_URL}?${qs.stringify({
                    ...googleMapsBaseOptions,
                    timestamp: googlePlacesTimestamp,
                })}`,
                document.querySelector('head'),
                'google-maps'
            ).then(() => {
                if (mounted) dispatch(googlePlacesInitialized(window?.google));
            });
        } else if (window?.google && !googleLibrary) {
            dispatch(googlePlacesInitialized(window.google));
        }

        return () => (mounted = false);
    }, [dispatch, googleLibrary, googlePlacesTimestamp]);

    const useDefaultStyle =
        !loggedIn ||
        cognitoResponse?.nextStep?.signInStep === userChallenges.NEW_PASSWORD_REQUIRED ||
        (userIsInvestor && hasFinishedSubscription && isNewSubscription) ||
        (userIsInvestor &&
            hasInProgressSubscription &&
            !subscriberSignedBS &&
            !hasFinishedSubscription) ||
        (userIsDistributor && hasInProgressSubscription && !distributorPreviewClientSpace) ||
        isCreatingSubscription;

    const showSidebar =
        loggedIn &&
        ((cognitoResponse?.nextStep?.signInStep &&
            cognitoResponse?.nextStep?.signInStep !== userChallenges.NEW_PASSWORD_REQUIRED) ||
            !cognitoResponse?.nextStep?.signInStep) &&
        !currentRouteIsPublic &&
        currentRouteIsProtected &&
        (userIsNetworkAdmin ? hasAssignedNetwork : true) &&
        !userIsInvestor &&
        !(userIsDistributor && hasInProgressSubscription && !distributorPreviewClientSpace) &&
        !isCreatingSubscription &&
        cognitoResponse?.nextStep?.signInStep !== userChallenges.NEW_PASSWORD_REQUIRED;

    const showFooter = useMemo(
        () =>
            loggedIn &&
            !userIsChallenged &&
            ((!userIsInvestor && !userIsDistributor) ||
                subscriberSignedBS ||
                distributorPreviewClientSpace ||
                hasFinishedSubscription) &&
            !location.pathname.includes('sign-up') &&
            !isNewSubscription,
        [
            distributorPreviewClientSpace,
            hasFinishedSubscription,
            isNewSubscription,
            location.pathname,
            loggedIn,
            subscriberSignedBS,
            userIsChallenged,
            userIsDistributor,
            userIsInvestor,
        ]
    );

    // Memoized render function to handle different app states
    const renderApp = useMemo(() => {
        if (getUserLoader) {
            return <BigLoader />;
        }

        return (
            <React.Fragment>
                <HubspotScript />
                <HubspotPageTracking />
                {/* TOP BAR */}
                {showAppBar && <TopBar />}

                {/* MAIN WRAPPER */}
                <Box
                    id="main-wrapper"
                    sx={theme => ({
                        width: '100%',
                        height: !showAppBar
                            ? '100%'
                            : `calc(100% - ${theme.constants.appBarHeight}px)`,
                        minHeight: `calc(100% - ${theme.constants.appBarHeight}px)`,
                        backgroundColor: useDefaultStyle
                            ? theme.palette.common.white
                            : theme.palette.grey[100],
                        display: 'flex',
                        flexDirection: 'row',
                        overflow: isTunnelFlow ? 'hidden' : 'auto',
                    })}
                >
                    <CustomNotification />
                    {!!internalUser && <NotificationDialog />}
                    <SignatureChoiceDialog />
                    <ConfirmLogoutDialog />

                    {/* SIDEBAR */}
                    {showSidebar && (
                        <Sidebar routes={[...filteredRoutes.filter(route => route.protected)]} />
                    )}

                    <Box
                        id="main-container-wrapper"
                        sx={theme => ({
                            flexGrow: 1,
                            background: useDefaultStyle
                                ? 'inherit'
                                : `${theme.palette.common.white}`,
                            width:
                                showSidebar || sidebarOpen
                                    ? `calc(100% - ${theme.constants.sidebarWidth}px)`
                                    : '100%',
                            minHeight: '100%',
                            height: showSidebar ? 'fit-content' : '100%',
                            paddingLeft: showSidebar && sidebarOpen ? '248px' : 0,
                            display: 'flex',
                            flexDirection: 'column',
                        })}
                    >
                        {/* MAIN CONTAINER */}
                        <MainContainer
                            loggedIn={loggedIn}
                            hasInProgressSubscription={hasInProgressSubscription}
                            hasFinishedSubscription={hasFinishedSubscription}
                            userIsServicer={userIsServicer}
                            userIsDistributor={userIsDistributor}
                            userIsInvestor={userIsInvestor}
                            internalUser={internalUser}
                            currentSubscription={currentSubscription}
                            filteredRoutes={filteredRoutes}
                            userChallenge={cognitoResponse?.nextStep?.signInStep}
                            showSidebar={showSidebar}
                            isTunnelFlow={isTunnelFlow}
                        />

                        {/* FOOTER */}
                        {showFooter && <Footer />}
                    </Box>
                </Box>
            </React.Fragment>
        );
    }, [
        getUserLoader,
        showAppBar,
        useDefaultStyle,
        isTunnelFlow,
        internalUser,
        showSidebar,
        sidebarOpen,
        filteredRoutes,
        loggedIn,
        hasInProgressSubscription,
        hasFinishedSubscription,
        userIsServicer,
        userIsDistributor,
        userIsInvestor,
        currentSubscription,
        cognitoResponse?.nextStep?.signInStep,
        showFooter,
    ]);

    return <CognitoContext.Provider value={cognitoContext}>{renderApp}</CognitoContext.Provider>;
}

export default App;
